import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { BankAccountSelectorF0Query } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { BankAccountSelectorF0 } from 'modules/banking/components/BankAccountSelectorF0'
import { useCreatePaymentForm } from 'modules/banking/components/CreateBulkPaymentForm/useForm'

type BankAccountOption = NonNullable<
  BankAccountSelectorF0Query['banking']
>['bankAccountsConnection']['edges'][number]['node']

export interface NewPaymentModalF0Props {
  onClose: () => void
  onSuccess: (id: number) => void
}

export const NewPaymentModalF0 = ({ onClose, onSuccess }: NewPaymentModalF0Props) => {
  const { form: creationForm, loading } = useCreatePaymentForm({ onSuccess })
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountOption | null>(null)

  const isValid =
    creationForm.isValid &&
    creationForm.values.label?.trim().length > 0 &&
    !!creationForm.values.bankAccountId

  const handleSelectBankAccount = (bankAccount: BankAccountOption | null) => {
    creationForm.setFieldValue('bankAccountId', bankAccount ? String(bankAccount.id) : '')
    setSelectedBankAccount(bankAccount)
  }

  const handleSubmit = () => {
    track(trackingEvents.accounting.payments.clickContinueBulkPayment)
    creationForm.submitForm()
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (loading) return
        onClose()
      }}
      title={i18n.t('banking.bulk_payment.new_bulk_payment_modal.title')}
      description={i18n.t('banking.bulk_payment.new_bulk_payment_modal.description')}
      primaryAction={{
        label: i18n.t('banking.bulk_payment.new_bulk_payment_modal.submit'),
        onClick: handleSubmit,
        disabled: !isValid || loading,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('banking.bulk_payment.new_bulk_payment_modal.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16" flexDirection="column">
        <Input
          required
          label={i18n.t('banking.bulk_payment.forms.fields.name.label')}
          placeholder={i18n.t('banking.bulk_payment.forms.fields.name.placeholder')}
          value={creationForm.values.label}
          onChange={(value) => creationForm.setFieldValue('label', value)}
          error={creationForm.errors.label}
        />
        <BankAccountSelectorF0
          value={selectedBankAccount}
          onChange={handleSelectBankAccount}
          accountNumberTypes={['iban']}
          providerService="sepa_payment_management"
          currency="EUR"
          required={!selectedBankAccount}
        />
        <Textarea
          label={i18n.t('banking.bulk_payment.forms.fields.opt_description.label')}
          placeholder={i18n.t('banking.bulk_payment.forms.fields.opt_description.placeholder')}
          value={creationForm.values.description}
          onChange={(value) => creationForm.setFieldValue('description', value)}
        />
      </Box>
    </F0Dialog>
  )
}
